import { createContext, CSSProperties, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { api, ApiError } from '../api/client';
import type { Meta, Role } from '../api/types';
import { fmtDate, levelTone, mute, skillTone, PRIORITY_LABEL, statusTone, priorityClass, RISK_LABEL, riskClass, STATUS_LABEL, statusClass, setTimezone } from '../lib/format';

// ---------- Meta / current user ----------
export function useMeta() {
  return useQuery({
    queryKey: ['meta'],
    queryFn: async () => { const m = await api.get<Meta>('/api/meta'); setTimezone(m.timezone); return m; },
    staleTime: 60_000,
  });
}
/**
 * What the signed-in user may do. Areas come from Roles & permissions (can('sales.view')); role is the built-in role,
 * kept for behaviour that is not an area (shop floor, own work only).
 */
export function useRole(): { role: Role | null; can: (perm: string) => boolean; canPlan: boolean; isAdmin: boolean; canForecast: boolean; canManage: boolean; canKit: boolean; switched: boolean } {
  const m = useMeta();
  const role = m.data?.user.role ?? null;
  const perms = new Set(m.data?.perms ?? []);
  const can = (p: string) => perms.has(p);
  return { role, can, canPlan: can('planning.change'), isAdmin: role === 'admin', canForecast: can('forecast.view'), canManage: can('skills.change'), canKit: can('kitting.change'), switched: !!m.data?.switched };
}

// ---------- Toasts ----------
type Toast = { id: number; kind: 'ok' | 'bad' | 'warn' | 'info'; text: string };
const ToastCtx = createContext<(kind: Toast['kind'], text: string) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t.slice(-3), { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'bad' || kind === 'warn' ? 8000 : 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.kind === 'ok' ? <CheckCircle2 size={16} /> : t.kind === 'bad' ? <XCircle size={16} /> : t.kind === 'warn' ? <AlertTriangle size={16} /> : <Info size={16} />}
            <span style={{ flex: 1 }}>{t.text}</span>
            <button className="btn ghost icon sm" onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))} aria-label="Dismiss"><X size={14} /></button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);
export const errText = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Something went wrong.');

// ---------- Overlays ----------
/** Open modals, oldest first: the newest is drawn on top and alone answers Escape. */
const modalStack: object[] = [];
let modalSeq = 0;

export function Modal({ title, onClose, children, footer, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const me = useRef<object>({});
  const [z] = useState(() => 100 + ++modalSeq);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const token = me.current;
    modalStack.push(token);
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape' && modalStack[modalStack.length - 1] === token) close.current(); };
    window.addEventListener('keydown', k);
    return () => {
      window.removeEventListener('keydown', k);
      const i = modalStack.indexOf(token);
      if (i >= 0) modalStack.splice(i, 1);
      if (!modalStack.length) modalSeq = 0;
    };
  }, []);
  // Drawn at the end of the page, so a dialog opened from another (eg a reason asked for during a change) is on top
  return createPortal(
    <div className="overlay" style={{ zIndex: z }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <div className="modal-head"><h2 style={{ margin: 0, flex: 1 }}>{title}</h2><button className="btn ghost icon" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Confirm({ title, message, confirmLabel = 'Confirm', danger, onConfirm, onClose }: { title: string; message: ReactNode; confirmLabel?: string; danger?: boolean; onConfirm: () => void; onClose: () => void }) {
  return (
    <Modal title={title} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className={`btn ${danger ? 'danger' : 'primary'}`} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</button></>}>
      <div>{message}</div>
    </Modal>
  );
}

/** Prompt for an override reason when a rule (skill, dependency) blocks a change. */
export function OverrideDialog({ message, onSubmit, onClose }: { message: string; onSubmit: (reason: string) => void; onClose: () => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal title="Override required" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn danger" disabled={reason.trim().length < 3} onClick={() => onSubmit(reason.trim())}>Override and save</button></>}>
      <div className="alert bad"><AlertTriangle size={16} /><span>{message}</span></div>
      <label className="field">Reason
        <textarea className="input" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="eg Supervised by Alice as part of training" />
      </label>
    </Modal>
  );
}

// ---------- Badges ----------
export const RiskBadge = ({ risk, title }: { risk: string; title?: string | null }) => <span className={`badge ${riskClass(risk)}`} title={title ?? undefined}>{RISK_LABEL[risk] ?? risk}</span>;
export const StatusBadge = ({ status }: { status: string }) => <span className={`badge ${statusClass(status)}`}>{STATUS_LABEL[status] ?? status}</span>;
export const PriorityBadge = ({ priority }: { priority: string }) => priority === 'normal' ? <span className="badge">Normal</span> : <span className={`badge ${priorityClass(priority)}`}>{PRIORITY_LABEL[priority]}</span>;

export function Loading({ lines = 4 }: { lines?: number }) {
  return <div className="col" style={{ padding: 20, gap: 12 }} aria-busy="true">{Array.from({ length: lines }).map((_, i) => <div key={i} className="skel" style={{ height: 14, width: `${90 - i * 12}%` }} />)}</div>;
}
export function ErrorBox({ error }: { error: unknown }) {
  return <div className="alert bad"><XCircle size={16} /><span>{errText(error)}</span></div>;
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return <div className="tabs" role="tablist">{tabs.map((t) => <button key={t.id} role="tab" aria-selected={value === t.id} className={value === t.id ? 'on' : ''} onClick={() => onChange(t.id)}>{t.label}</button>)}</div>;
}

export function Seg<T extends string>({ options, value, onChange, label }: { options: { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void; label?: string }) {
  return <div className="seg" role="group" aria-label={label}>{options.map((o) => <button key={o.id} className={value === o.id ? 'on' : ''} onClick={() => onChange(o.id)} aria-pressed={value === o.id}>{o.label}</button>)}</div>;
}

/** Job workflow status: a dot in the status colour and the name, on a neutral chip. */
export function WorkflowChip({ id }: { id: string | null | undefined }) {
  const m = useMeta();
  const s = m.data?.statuses.find((x) => x.id === id);
  if (!s) return null;
  const t = statusTone(s.name, s.colour);
  return <span className="wf-chip" style={{ background: t.bg, color: t.text }}>{s.name}</span>;
}

/** A person: avatar (initials in their colour), name and one line of muted detail. */
export function Person({ name, initials, colour, meta, size }: { name: ReactNode; initials: string; colour?: string | null; meta?: ReactNode; size?: 'sm' | 'lg' }) {
  return (
    <span className="person">
      <span className={`avatar ${size ?? ''}`} style={{ background: colour ?? undefined }}>{initials}</span>
      <span style={{ minWidth: 0 }}>
        <span className="p-name" style={{ display: 'block' }}>{name}</span>
        {meta && <span className="p-meta" style={{ display: 'block' }}>{meta}</span>}
      </span>
    </span>
  );
}

/** Utilisation as a compact pill: quiet when normal, amber when near capacity, red when over. */
export function UtilPill({ pct, over, near, title }: { pct: number | null; over?: boolean; near?: boolean; title?: string }) {
  return <span className={`util-pill ${over ? 'over' : near ? 'near' : ''}`} title={title}>{pct == null ? '–' : `${pct}%`}</span>;
}

/** Thin utilisation meter. */
export function Meter({ pct }: { pct: number }) {
  const cls = pct > 100 ? 'over' : pct >= 90 ? 'near' : '';
  return <span className={`meter ${cls}`}><span style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} /></span>;
}

/** Skill level as a coloured label (level scale from the application palette). */
export function LevelLabel({ level, max, children }: { level: number; max: number; children?: ReactNode }) {
  const t = levelTone(level, max);
  return <span className="badge" style={{ background: t.bg, color: t.text }}>{children ?? level}</span>;
}

/** Proficiency as rating dots (level out of max). */
export function Pips({ level, max = 3 }: { level: number; max?: number }) {
  return <span className="pips" aria-label={`Level ${level} of ${max}`} data-level={level}>{Array.from({ length: max }).map((_, i) => <i key={i} className={i < level ? 'on' : ''} />)}</span>;
}

/**
 * A job's due date. Assemblies raised in Unleashed without an Assemble By date have none: they show
 * "No due date" and, once scheduled, the planned finish instead.
 */
export function DueDate({ date, planned, late, prefix = '' }: { date: string | null | undefined; planned?: string | null; late?: boolean; prefix?: string }) {
  if (date) return <span className={late ? 'bad-text' : ''}>{prefix}{fmtDate(date)}</span>;
  return (
    <span className="due-none" title="No due date">
      <span className="faint">No due date</span>
      {planned && <span className="due-planned">Planned {fmtDate(planned)}</span>}
    </span>
  );
}

export function SkillDot({ colour, name }: { colour?: string | null; name?: string | null }) {
  return <span className="dot" style={{ background: name ? skillTone(name).bg : colour ? mute(colour) : 'var(--text-4)' }} />;
}

export function PageHead({ title, desc, children }: { title: string; desc?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div style={{ minWidth: 0 }}><h1>{title}</h1>{desc && <div className="desc">{desc}</div>}</div>
      <div className="spacer" />
      {children && <div className="actions">{children}</div>}
    </div>
  );
}

export type BrandView = { appName: string; wordLight: string; wordBold: string; sub: string; colour: string; show: 'wordmark' | 'logo'; logo: { fileName: string; version: number } | null };
/** The install's name, wordmark and logo (Administration → Company and brand). Public: the sign-in page reads it. */
export function useBrand() {
  return useQuery({ queryKey: ['brand'], queryFn: () => api.get<BrandView>('/api/brand'), staleTime: 300_000 });
}
export const brandLogoUrl = (b: BrandView | undefined) => (b?.logo ? `/api/brand/logo?v=${b.logo.version}` : null);

/** The install's wordmark: a light part and a bold part with a rule beneath, and a line under it; or its logo. */
export function Wordmark({ light, size }: { light?: boolean; size?: 'lg' }) {
  const b = useBrand().data;
  if (!b) return <span className={['wordmark', size ?? ''].filter(Boolean).join(' ')} />;
  const logo = b.show === 'logo' ? brandLogoUrl(b) : null;
  if (logo) return <img className={['brand-logo', size ?? ''].filter(Boolean).join(' ')} src={logo} alt={b.appName} />;
  return (
    <span className={['wordmark', light ? 'light' : '', size ?? ''].filter(Boolean).join(' ')} aria-label={[`${b.wordLight}${b.wordBold}`, b.sub].filter(Boolean).join(' ')}
      style={{ '--brand': b.colour } as CSSProperties}>
      <span className="wm"><span className="wl">{b.wordLight}</span><span className="wb">{b.wordBold}</span></span>
      <span className="rule" />
      {b.sub && <span className="subbrand">{b.sub}</span>}
    </span>
  );
}

export function download(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
